import { useEffect, useState } from "react";
import { ReactComponent as CreditCoinIcon } from "../../../assets/images/credit-coin.svg";
import styles from "../../../styles/components/CreditsSection.module.css";
import StripeCheckoutModal from "../billing/StripeCheckoutModal";
import {
  getAccountEntitlement,
  getRechargePackages,
  createRechargeCheckout,
} from "../../../services/billing.service";
import {
  CREDIT_KPI_CODES,
  CREDIT_CHIP_GRADIENTS,
  DEFAULT_CREDIT_CHIP_GRADIENT,
} from "../../../constants/creditKpis";

const KPI_TYPE_ORDER = CREDIT_KPI_CODES;

function sumAvailableCredits(entitlementData) {
  if (!entitlementData?.entitlements) return 0;
  return Object.values(entitlementData.entitlements)
    .filter((kpi) => !kpi.is_unlimited && (kpi.type === "counter" || kpi.type === "limit"))
    .reduce((total, kpi) => total + (Number(kpi.available) || 0), 0);
}

function CreditChip({ count, kpiTypeCode }) {
  const background =
    CREDIT_CHIP_GRADIENTS[kpiTypeCode] || DEFAULT_CREDIT_CHIP_GRADIENT;
  return (
    <div className={styles.creditChip} style={{ background, boxShadow: "0px 1px 0px #1A1A1B" }}>
      <CreditCoinIcon width={12} height={12} />
      <span className={styles.creditChipCount}>{count}</span>
    </div>
  );
}

function formatDate(dateString) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function KpiCard({ kpi }) {
  const isCounter = kpi.type === "counter" || kpi.type === "limit";
  const usedPercent =
    isCounter && kpi.limit > 0
      ? Math.min(100, Math.round((kpi.used / kpi.limit) * 100))
      : 0;

  return (
    <div className={styles.kpiCard}>
      <div className={styles.kpiName}>{kpi.name}</div>

      {kpi.type === "boolean" ? (
        <div
          className={`${styles.kpiBooleanBadge} ${
            kpi.enabled ? styles.kpiBooleanEnabled : styles.kpiBooleanDisabled
          }`}
        >
          {kpi.enabled ? "Habilitado" : "Deshabilitado"}
        </div>
      ) : isCounter ? (
        <>
          <div className={styles.kpiNumbers}>
            {kpi.is_unlimited ? (
              <span className={styles.kpiUnlimited}>Ilimitado</span>
            ) : (
              <>
                <span className={styles.kpiUsed}>
                  {(kpi.used ?? 0).toLocaleString("en-US")}
                </span>
                <span className={styles.kpiSeparator}>/</span>
                <span className={styles.kpiLimit}>
                  {(kpi.limit ?? 0).toLocaleString("en-US")}
                </span>
              </>
            )}
          </div>

          {!kpi.is_unlimited && (
            <>
              <div className={styles.progressBar}>
                <div
                  className={styles.progressFill}
                  style={{ width: `${usedPercent}%` }}
                />
              </div>
              <div className={styles.kpiAvailable}>
                {(kpi.available ?? 0).toLocaleString("en-US")} disponibles
                {(kpi.extra_balance ?? 0) > 0 && (
                  <span className={styles.kpiExtraBalance}>
                    {" "}(+{kpi.extra_balance.toLocaleString("en-US")} extra)
                  </span>
                )}
              </div>
            </>
          )}
        </>
      ) : null}
    </div>
  );
}

function PackageColumn({ kpiTypeCode, packages, selectedAccountId, onBuyPackage, checkoutLoadingCode }) {
  if (!packages?.length) return null;

  const typeName = packages[0]?.kpi_type_name || kpiTypeCode;

  return (
    <div className={styles.packageColumn}>
      <div className={styles.packageColumnTitle}>{typeName}</div>
      <div className={styles.packageList}>
        {packages.map((pkg) => {
          const isLoading = checkoutLoadingCode === pkg.code;
          return (
            <div key={pkg.code} className={styles.packageRow}>
              <CreditChip count={pkg.credits} kpiTypeCode={kpiTypeCode} />
              <button
                type="button"
                className={styles.packageBuyButton}
                disabled={!selectedAccountId || isLoading}
                onClick={() => onBuyPackage(pkg.code)}
              >
                {isLoading
                  ? "Procesando..."
                  : `Comprar por ${Number(pkg.price).toLocaleString("en-US")} ${(pkg.currency || "USD").toUpperCase()}`}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CreditsSection = ({ accounts = [] }) => {
  const [selectedAccount, setSelectedAccount] = useState(null);
  const singleAccount = accounts.length === 1 ? accounts[0] : null;

  const [entitlement, setEntitlement] = useState(null);
  const [entitlementLoading, setEntitlementLoading] = useState(false);
  const [entitlementError, setEntitlementError] = useState("");

  const [packages, setPackages] = useState({});
  const [packagesLoading, setPackagesLoading] = useState(false);

  const [checkoutLoadingCode, setCheckoutLoadingCode] = useState(null);
  const [checkoutError, setCheckoutError] = useState("");
  const [checkoutClientSecret, setCheckoutClientSecret] = useState("");
  const [checkoutModalOpen, setCheckoutModalOpen] = useState(false);

  useEffect(() => {
    if (
      selectedAccount &&
      !accounts.some((account) => account.id === selectedAccount.id)
    ) {
      setSelectedAccount(null);
      return;
    }
    if (singleAccount && !selectedAccount) {
      setSelectedAccount(singleAccount);
    }
  }, [accounts, singleAccount, selectedAccount]);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setPackagesLoading(true);
        const data = await getRechargePackages();
        setPackages(data || {});
      } catch (err) {
        console.error("Error cargando paquetes:", err);
      } finally {
        setPackagesLoading(false);
      }
    };

    fetchPackages();
  }, []);

  useEffect(() => {
    if (!selectedAccount?.id) return;

    const fetchEntitlement = async () => {
      try {
        setEntitlementLoading(true);
        setEntitlementError("");
        const data = await getAccountEntitlement(selectedAccount.id);
        setEntitlement(data);
      } catch (err) {
        console.error("Error cargando créditos:", err);
        setEntitlementError("No se pudo cargar la información de créditos.");
      } finally {
        setEntitlementLoading(false);
      }
    };

    fetchEntitlement();
  }, [selectedAccount]);

  const handleBuyPackage = async (packageCode) => {
    if (!selectedAccount?.id) return;
    try {
      setCheckoutLoadingCode(packageCode);
      setCheckoutError("");
      const result = await createRechargeCheckout({
        account_id: selectedAccount.id,
        package_code: packageCode,
      });
      const clientSecret = result?.client_secret;
      if (!clientSecret) throw new Error("No se recibió client_secret.");
      setCheckoutClientSecret(clientSecret);
      setCheckoutModalOpen(true);
    } catch (err) {
      console.error("Error iniciando checkout:", err);
      setCheckoutError(err.message || "No se pudo iniciar la compra.");
    } finally {
      setCheckoutLoadingCode(null);
    }
  };

  const packageEntries = Object.entries(packages).sort(([codeA], [codeB]) => {
    const indexA = KPI_TYPE_ORDER.indexOf(codeA);
    const indexB = KPI_TYPE_ORDER.indexOf(codeB);
    const orderA = indexA === -1 ? KPI_TYPE_ORDER.length : indexA;
    const orderB = indexB === -1 ? KPI_TYPE_ORDER.length : indexB;
    return orderA - orderB;
  });
  const kpiEntries = entitlement?.entitlements
    ? Object.entries(entitlement.entitlements).sort(([codeA], [codeB]) => {
        const indexA = KPI_TYPE_ORDER.indexOf(codeA);
        const indexB = KPI_TYPE_ORDER.indexOf(codeB);
        const orderA = indexA === -1 ? KPI_TYPE_ORDER.length : indexA;
        const orderB = indexB === -1 ? KPI_TYPE_ORDER.length : indexB;
        return orderA - orderB;
      })
    : [];

  if (!selectedAccount) {
    return (
      <div className={styles.sectionContent}>
        <div className={styles.accountsList}>
          {accounts.length === 0 && (
            <div className={styles.emptyState}>No hay cuentas disponibles.</div>
          )}
          {accounts.map((account) => (
            <div key={account.id} className={styles.accountItem}>
              <button
                type="button"
                className={styles.accountCardButton}
                onClick={() => setSelectedAccount(account)}
              >
                <div className={styles.accountCard}>
                  <div className={styles.accountLeft}>
                    <div className={styles.accountLogo}>
                      {(account.name || "?").charAt(0).toUpperCase()}
                    </div>
                    <div className={styles.accountInfo}>
                      <div className={styles.accountName}>
                        {account.name || "Sin nombre"}
                      </div>
                    </div>
                  </div>
                  <span className={styles.linkButton}>Ver créditos</span>
                </div>
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.creditsScreen}>
      {!singleAccount && (
        <button
          type="button"
          className={styles.backButton}
          onClick={() => setSelectedAccount(null)}
        >
          ← {selectedAccount.name}
        </button>
      )}

      {entitlementLoading ? (
        <div className={styles.stateBox}>Cargando...</div>
      ) : entitlementError ? (
        <div className={styles.stateBoxError}>{entitlementError}</div>
      ) : entitlement ? (
        <>
          <div className={styles.planInfo}>
            <span className={styles.planName}>
              {entitlement.plan || entitlement.last_plan || "Sin plan"}
            </span>
            <span className={styles.planValid}>
              Válido hasta {formatDate(entitlement.valid_until)}
            </span>
          </div>

          {kpiEntries.length === 0 ? (
            <div className={styles.emptyState}>Sin créditos disponibles.</div>
          ) : (
            <div className={styles.kpiGrid}>
              {kpiEntries.map(([code, kpi]) => (
                <KpiCard key={code} kpi={kpi} />
              ))}
            </div>
          )}
        </>
      ) : null}

      {packageEntries.length > 0 && (
        <div className={styles.rechargeSection}>
          <div className={styles.rechargeSectionHeader}>
            <div className={styles.rechargeSectionTitle}>
              ¿Te has quedado sin créditos?
            </div>
            <div className={styles.rechargeSectionSubtitle}>
              Compra un pack para seguir usando la plataforma sin cambiar de plan. Es una compra única, no una suscripción. Los créditos extra se usan cuando se terminan los del plan y son válidos hasta el final de tu ciclo de facturación.
            </div>
          </div>

          {checkoutError && (
            <div className={styles.stateBoxError}>{checkoutError}</div>
          )}

          {packagesLoading ? (
            <div className={styles.stateBox}>Cargando paquetes...</div>
          ) : (
            <div className={styles.packageColumns}>
              {packageEntries.map(([kpiTypeCode, pkgs]) => (
                <PackageColumn
                  key={kpiTypeCode}
                  kpiTypeCode={kpiTypeCode}
                  packages={pkgs}
                  selectedAccountId={selectedAccount?.id}
                  onBuyPackage={handleBuyPackage}
                  checkoutLoadingCode={checkoutLoadingCode}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <StripeCheckoutModal
        open={checkoutModalOpen}
        title="Comprar créditos"
        clientSecret={checkoutClientSecret}
        onClose={() => {
          setCheckoutModalOpen(false);
          setCheckoutClientSecret("");
        }}
        onDone={async () => {
          setCheckoutModalOpen(false);
          setCheckoutClientSecret("");
          const creditsBefore = sumAvailableCredits(entitlement);
          for (let attempt = 0; attempt < 5; attempt++) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
            try {
              const data = await getAccountEntitlement(selectedAccount.id);
              setEntitlement(data);
              if (sumAvailableCredits(data) > creditsBefore) break;
            } catch {
              break;
            }
          }
        }}
      />
    </div>
  );
};

export default CreditsSection;
